import React, { useEffect, useMemo, useRef, useState } from 'react'

import cx from 'classnames'

import { Icon } from '@driftt/tide-icons'

import { useRefreshingInsightsCount } from 'hooks/useInsightsFeed'
import { FilterPage } from 'utils/filters'

import utilityStyles from '../../styles/utility.module.css'
import styles from './styles.module.css'

/**
 *
 * @param element {HTMLElement | null} - the element being evaluated to determine if it's in the viewport
 * @returns {boolean} - determines if the element is currently visible in the viewport
 *
 * @example
 *
 * const myRef = useRef<TYPE>(null)
 * const isVisible = useIsInViewPort(myRef.current)
 */
function useIsInViewport(element: HTMLElement | null) {
  // set to true initially for elements that have not rendered yet
  const [isVisible, setIsVisible] = useState<boolean>(true)

  const observer = useMemo(
    () => new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting)),
    []
  )

  useEffect(() => {
    if (element) {
      observer.observe(element)
    }

    return () => {
      observer.disconnect()
    }
  }, [element, observer])

  return isVisible
}

interface RefreshActivityProps {
  prevCount?: number
  filterPage: FilterPage
  withDateRange?: boolean
  legacyInsightsOnly?: boolean
  accountId?: string
  onClick: () => void
  fixedWhenScrolled?: boolean
}

const RefreshActivity: React.FC<RefreshActivityProps> = ({
  prevCount = 0,
  filterPage,
  withDateRange = true,
  legacyInsightsOnly = false,
  accountId,
  onClick,
  fixedWhenScrolled
}) => {
  const latestCount = useRefreshingInsightsCount(
    filterPage,
    withDateRange,
    legacyInsightsOnly,
    accountId
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const isInViewport = useIsInViewport(containerRef.current)

  const numNewerResults = (latestCount || 0) - prevCount

  if (numNewerResults <= 0) {
    return null
  }

  return (
    <div ref={containerRef} className={styles.refreshButtonContainer}>
      <button
        className={cx(styles.refreshResultsButton, utilityStyles.semiBoldWeight, {
          [styles.refreshButtonFixed]: fixedWhenScrolled && !isInViewport
        })}
        onClick={onClick}>
        <Icon className={styles.refreshIcon} name="refresh-2" width={11} height={11} />
        {numNewerResults} new activity {numNewerResults === 1 ? 'group' : 'groups'}
      </button>
    </div>
  )
}

export default RefreshActivity
